import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import dashboardPreview from "@/assets/dashboard-hero-preview.jpg";

const ROTATING_WORDS = ["Facebook", "LinkedIn", "Instagram", "TikTok", "YouTube", "Twitter/X"];

export default function HeroSection() {
  const [wordIndex, setWordIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setWordIndex((i) => (i + 1) % ROTATING_WORDS.length);
        setVisible(true);
      }, 300);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-x-hidden w-full">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-violet-950 to-slate-950">
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(ellipse at 25% 40%, rgba(139,92,246,0.35) 0%, transparent 55%), radial-gradient(ellipse at 75% 60%, rgba(217,70,239,0.25) 0%, transparent 55%), radial-gradient(ellipse at 50% 0%, rgba(99,102,241,0.2) 0%, transparent 50%)" }} />
      </div>

      {/* Animated orbs */}
      <div className="absolute top-1/4 left-1/6 w-72 h-72 bg-violet-600/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/3 right-1/6 w-96 h-96 bg-fuchsia-600/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1.5s" }}></div>
      <div className="absolute top-2/3 left-1/2 w-56 h-56 bg-pink-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "3s" }}></div>

      {/* Floating social icons — two layouts so nothing ever sits behind text:
          - Mobile (<md): only a small ring of icons around the very edges, no
            icons in the middle area where the title/description live.
          - Desktop (md+): the original scattered look the user approved. */}
      {/* DESKTOP — scattered, tilted, varied sizes (UNTOUCHED on user request) */}
      {[
        { icon: "ri-facebook-fill",     color: "#1877F2", top: "14%",  left: "6%",   rotate: "-12deg", size: 56, delay: "0s" },
        { icon: "ri-instagram-fill",    color: "#E1306C", top: "8%",   right: "8%",  rotate: "10deg",  size: 64, delay: "0.4s" },
        { icon: "ri-tiktok-fill",       color: "#ffffff", top: "22%",  right: "18%", rotate: "-8deg",  size: 44, delay: "1s" },
        { icon: "ri-youtube-fill",      color: "#FF0000", top: "30%",  left: "12%",  rotate: "14deg",  size: 50, delay: "1.6s" },
        { icon: "ri-twitter-x-fill",    color: "#ffffff", top: "44%",  left: "4%",   rotate: "-6deg",  size: 38, delay: "2s" },
        { icon: "ri-linkedin-fill",     color: "#0A66C2", top: "40%",  right: "5%",  rotate: "8deg",   size: 52, delay: "0.8s" },
        { icon: "ri-pinterest-fill",    color: "#E60023", top: "60%",  right: "12%", rotate: "-14deg", size: 42, delay: "2.4s" },
        { icon: "ri-reddit-fill",       color: "#FF4500", top: "70%",  left: "8%",   rotate: "12deg",  size: 48, delay: "1.2s" },
        { icon: "ri-snapchat-fill",     color: "#FFFC00", top: "78%",  right: "6%",  rotate: "-10deg", size: 44, delay: "2.8s" },
        { icon: "ri-discord-fill",      color: "#5865F2", top: "86%",  left: "16%",  rotate: "6deg",   size: 40, delay: "1.8s" },
        { icon: "ri-telegram-fill",     color: "#26A5E4", top: "88%",  right: "20%", rotate: "-12deg", size: 38, delay: "0.6s" },
        { icon: "ri-medium-fill",       color: "#ffffff", top: "54%",  left: "16%",  rotate: "10deg",  size: 36, delay: "3.2s" },
      ].map((s, i) => (
        <div
          key={i}
          className="hidden md:flex absolute z-0 items-center justify-center rounded-2xl bg-white/8 backdrop-blur-sm border border-white/10 shadow-2xl shadow-black/30 animate-pulse pointer-events-none"
          style={{
            top: s.top,
            left: s.left,
            right: s.right,
            width: s.size,
            height: s.size,
            transform: `rotate(${s.rotate})`,
            animationDelay: s.delay,
            animationDuration: "4s",
          }}
        >
          <i className={s.icon} style={{ color: s.color, fontSize: s.size * 0.55 }}></i>
        </div>
      ))}

      {/* MOBILE — strict edge ring only. Top icons hug the very top of the
          screen above the announcement badge; bottom icons sit just under the
          stats row above the dashboard preview; side icons stay tight to the
          screen edge. NOTHING is placed where the title/description text lives. */}
      {[
        // Top row — above the announcement badge (top: 4%–8%)
        { icon: "ri-facebook-fill",  color: "#1877F2", top: "4%",  left: "4%",  rotate: "-12deg", size: 30 },
        { icon: "ri-instagram-fill", color: "#E1306C", top: "3%",  right: "4%", rotate: "10deg",  size: 34 },
        { icon: "ri-youtube-fill",   color: "#FF0000", top: "8%",  left: "26%", rotate: "8deg",   size: 26 },
        { icon: "ri-tiktok-fill",    color: "#ffffff", top: "8%",  right: "26%",rotate: "-6deg",  size: 26 },
        // Far-edge side icons — flush against screen sides, well away from centered text
        { icon: "ri-twitter-x-fill", color: "#ffffff", top: "30%", left: "2%",  rotate: "-6deg",  size: 24 },
        { icon: "ri-linkedin-fill",  color: "#0A66C2", top: "30%", right: "2%", rotate: "8deg",   size: 28 },
        { icon: "ri-pinterest-fill", color: "#E60023", top: "46%", right: "2%", rotate: "-12deg", size: 24 },
        { icon: "ri-reddit-fill",    color: "#FF4500", top: "46%", left: "2%",  rotate: "12deg",  size: 26 },
        // Bottom row — below the stats row, above the dashboard preview frame
        { icon: "ri-snapchat-fill",  color: "#FFFC00", top: "62%", left: "6%",  rotate: "-10deg", size: 26 },
        { icon: "ri-discord-fill",   color: "#5865F2", top: "62%", right: "6%", rotate: "6deg",   size: 28 },
      ].map((s, i) => (
        <div
          key={`m-${i}`}
          className="flex md:hidden landscape:hidden md:landscape:flex absolute z-0 items-center justify-center rounded-xl bg-white/8 backdrop-blur-sm border border-white/10 shadow-lg shadow-black/30 pointer-events-none"
          style={{
            top: s.top,
            left: s.left,
            right: s.right,
            width: s.size,
            height: s.size,
            transform: `rotate(${s.rotate})`,
          }}
        >
          <i className={s.icon} style={{ color: s.color, fontSize: s.size * 0.6 }}></i>
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 md:px-8 pt-28 pb-16 text-center">
        {/* Announcement badge */}
        <a
          href="/get-started"
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-orange-500 via-amber-400 to-yellow-400 text-slate-900 text-xs font-bold mb-8 hover:brightness-110 transition-all cursor-pointer shadow-lg shadow-orange-500/40 border border-orange-300/40"
        >
          <span className="w-2 h-2 rounded-full bg-slate-900 animate-pulse"></span>
          <strong>New:</strong>&nbsp;Instagram Reels &amp; TikTok auto-posting now live
          <i className="ri-arrow-right-line text-slate-900"></i>
        </a>

        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 max-w-5xl mx-auto" style={{ lineHeight: 1.15 }}>
          Grow Your Skool Group<br />
          <span className="inline">
            <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400 bg-clip-text text-transparent">
              Automatically on{" "}
            </span>
            <span
              className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-500 bg-clip-text text-transparent transition-opacity duration-300"
              style={{
                opacity: visible ? 1 : 0,
                fontFamily: "'Kaushan Script', cursive",
                fontSize: "1.1em",
                letterSpacing: "0.01em",
                display: "inline-block",
                paddingRight: "0.1em",
              }}
            >
              {ROTATING_WORDS[wordIndex]}
            </span>
          </span>
        </h1>

        <p className="text-lg md:text-xl text-white/65 max-w-2xl mx-auto mb-10 leading-relaxed">
          ClickSendAI automatically analyzes your Skool Group content, generates AI-powered Marketing Material, and auto-publishes it to&nbsp;<strong className="text-white/90">30+ Social, Video &amp; Image Sites &amp; Directories including Business Groups &amp; Directories</strong> — Growing your business FOR YOU!!
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <Link
            to="/get-started"
            className="group px-8 py-4 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white font-semibold text-base hover:opacity-90 transition-all whitespace-nowrap cursor-pointer shadow-2xl shadow-violet-500/40 flex items-center gap-2"
          >
            Start Free 7-Day Trial
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
          </Link>
          <Link
            to="/features"
            className="px-8 py-4 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold text-base hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
          >
            <i className="ri-play-circle-line text-fuchsia-400"></i>Watch 2-min Demo
          </Link>
        </div>

        {/* Trust signals row */}
        <div className="flex flex-wrap items-center justify-center gap-4 mb-4">
          {[
            "7-day free trial",
            "Cancel anytime",
            "Setup in under 5 minutes",
          ].map((item) => (
            <div key={item} className="flex items-center gap-1.5 text-white/60 text-xs">
              <i className="ri-check-line text-emerald-400 text-sm"></i>
              {item}
            </div>
          ))}
        </div>

        {/* Stats row */}
        <div className="flex flex-wrap items-center justify-center gap-8 mb-14 mt-8">
          {[
            { value: "30+", label: "Sites in Network", icon: "ri-global-line" },
            { value: "7-Day", label: "Free Trial", icon: "ri-calendar-check-line" },
            { value: "24/7", label: "Fully Automated", icon: "ri-robot-2-fill" },
          ].map((stat) => (
            <div key={stat.label} className="flex flex-col items-center gap-1">
              <div className="flex items-center gap-1.5">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${stat.icon} text-violet-400 text-sm`}></i>
                </div>
                <span className="text-2xl font-black text-white">{stat.value}</span>
              </div>
              <span className="text-xs text-white/50">{stat.label}</span>
            </div>
          ))}
        </div>

        {/* Dashboard preview */}
        <div className="relative max-w-5xl mx-auto">
          <div className="absolute -inset-6 bg-gradient-to-r from-violet-600/25 to-fuchsia-600/25 rounded-3xl blur-2xl"></div>
          <div className="relative rounded-2xl overflow-hidden border border-white/15 shadow-2xl">
            {/* Fake browser chrome */}
            <div className="bg-slate-800/90 backdrop-blur-sm px-4 py-3 flex items-center gap-2 border-b border-white/10">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400/70"></div>
                <div className="w-3 h-3 rounded-full bg-amber-400/70"></div>
                <div className="w-3 h-3 rounded-full bg-emerald-400/70"></div>
              </div>
              <div className="flex-1 mx-4 bg-slate-700/60 rounded-md px-3 py-1 text-xs text-slate-400 text-left">
                app.clicksendai.com/dashboard
              </div>
            </div>
            <img
              src={dashboardPreview}
              alt="ClickSendAI marketing automation dashboard preview"
              className="w-full h-auto block"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-transparent pointer-events-none"></div>
          </div>

          {/* Floating stat cards */}
          <div className="absolute -left-4 md:-left-8 top-1/3 bg-white rounded-xl p-3 shadow-xl border border-gray-100 hidden md:block">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100">
                <i className="ri-arrow-up-line text-emerald-600 text-sm"></i>
              </div>
              <div>
                <div className="text-xs font-bold text-gray-900">+847 members</div>
                <div className="text-xs text-gray-400">This month</div>
              </div>
            </div>
          </div>

          <div className="absolute -right-4 md:-right-8 top-1/2 bg-white rounded-xl p-3 shadow-xl border border-gray-100 hidden md:block">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-violet-100">
                <i className="ri-robot-2-line text-violet-600 text-sm"></i>
              </div>
              <div>
                <div className="text-xs font-bold text-gray-900">AI Generated</div>
                <div className="text-xs text-gray-400">24 posts today</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
